import { ReactNode } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { Navbar } from './Navbar';
import { Sidebar } from './Sidebar';

interface LayoutProps {
  children?: ReactNode;
}

// Main Learner App Layout
export function LearnerLayout({ children }: LayoutProps) {
  return (
    <div className="min-h-screen bg-cream-200">
      <Navbar />
      <main className="pt-16 pb-20 md:pb-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          {children || <Outlet />}
        </div>
      </main>
    </div>
  );
}

// Creator Portal Layout
export function CreatorLayout({ children }: LayoutProps) {
  return (
    <div className="min-h-screen bg-cream-200">
      <Sidebar portal="creator" />
      <main className="ml-64 p-8">
        <div className="max-w-6xl">
          {children || <Outlet />}
        </div>
      </main>
    </div>
  );
}

// Admin Portal Layout
export function AdminLayout({ children }: LayoutProps) {
  return (
    <div className="min-h-screen bg-cream-200">
      <Sidebar portal="admin" />
      <main className="ml-64 p-8">
        <div className="max-w-7xl">
          {children || <Outlet />}
        </div>
      </main>
    </div>
  );
}

// Organization (B2B) Portal Layout
interface OrgLayoutProps extends LayoutProps {
  orgName?: string;
}

export function OrgLayout({ children, orgName }: OrgLayoutProps) {
  return (
    <div className="min-h-screen bg-cream-200">
      <Sidebar portal="org" subtitle={orgName} />
      <main className="ml-64 p-8">
        <div className="max-w-7xl">
          {children || <Outlet />}
        </div>
      </main>
    </div>
  );
}

// Auth Layout (for login/signup pages)
export function AuthLayout({ children }: LayoutProps) {
  return (
    <div className="min-h-screen bg-cream-200 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link
          to="/"
          className="block text-center mb-8 hover:opacity-80 transition-opacity"
          aria-label="PulseMD home"
        >
          <span className="text-5xl">💚</span>
          <h1 className="text-3xl font-bold text-gradient mt-4">PulseMD</h1>
          <p className="text-charcoal-400 mt-2">
            5 minutes a day. Stay sharp, stay calm.
          </p>
        </Link>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-cream-300 p-8">
          {children || <Outlet />}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-charcoal-400 mt-6">
          By continuing, you agree to our{' '}
          <a href="/terms" className="text-primary-500 hover:underline">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" className="text-primary-500 hover:underline">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
}

// Simple Layout (for landing pages, etc.)
export function SimpleLayout({ children }: LayoutProps) {
  return (
    <div className="min-h-screen bg-cream-200">
      {children || <Outlet />}
    </div>
  );
}

// Page Header Component
interface PageHeaderProps {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  className?: string;
}

export function PageHeader({ title, subtitle, action, className }: PageHeaderProps) {
  return (
    <div className={clsx('flex items-start justify-between mb-8', className)}>
      <div>
        <h1 className="text-2xl font-bold text-charcoal-600">{title}</h1>
        {subtitle && (
          <p className="text-charcoal-400 mt-1">{subtitle}</p>
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

export default LearnerLayout;
